import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";

import devuelveDependenciaConstultada from "../tools/devuelveDependencia";
import devuelveSede from "../tools/devuelveSede";

const estilos = {
  contenedor: {
    padding: 20,
    background: "#f4f6fa",
    minHeight: "100vh",
    color: "#243247",
    fontFamily: "Arial, sans-serif"
  },

  tarjeta: {
    background: "#fff",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 2px 10px rgba(0,0,0,.06)",
    marginBottom: 16
  },

  input: {
    padding: "9px 11px",
    border: "1px solid #d5dce5",
    borderRadius: 7,
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    color: "#243247"
  },

  boton: {
    border: 0,
    borderRadius: 7,
    padding: "10px 16px",
    background: "#164b7e",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600
  },

  botonExcel: {
    border: 0,
    borderRadius: 7,
    padding: "10px 16px",
    background: "#16805d",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600
  },

  th: {
    padding: 10,
    background: "#edf2f8",
    textAlign: "left",
    borderBottom: "1px solid #dce4ef",
    whiteSpace: "nowrap"
  },

  td: {
    padding: 10,
    borderBottom: "1px solid #e7ebf1",
    verticalAlign: "top"
  }
};

const numero = valor =>
  Number(valor || 0).toLocaleString("es-AR");

const decimal = valor =>
  valor === null || valor === undefined
    ? "—"
    : Number(valor).toLocaleString("es-AR", {
        maximumFractionDigits: 2
      });

const fechaAR = valor => {
  if (!valor) return "—";

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return String(valor);
  }

  return fecha.toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const fechaLocal = fecha => {
  const partes = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(fecha);

  const valor = tipo =>
    partes.find(p => p.type === tipo)?.value;

  return `${valor("year")}-${valor("month")}-${valor("day")}`;
};

const normalizarExcel = filas =>
  filas.map(fila => {
    const resultado = {};

    for (const [campo, valor] of Object.entries(fila)) {
      if (valor === null || valor === undefined) {
        resultado[campo] = "";
      } else if (typeof valor === "object") {
        resultado[campo] = JSON.stringify(valor);
      } else {
        resultado[campo] = valor;
      }
    }

    return resultado;
  });

const agregarHoja = (libro, nombre, filas) => {
  const datos = normalizarExcel(filas || []);

  const hoja = XLSX.utils.json_to_sheet(
    datos.length
      ? datos
      : [{ Información: "Sin registros" }]
  );

  const columnas = Object.keys(
    datos[0] || { Información: "" }
  );

  hoja["!cols"] = columnas.map(columna => ({
    wch: Math.min(
      65,
      Math.max(
        16,
        columna.length + 3,
        ...datos.slice(0, 100).map(fila =>
          Math.min(
            65,
            String(fila[columna] ?? "").length + 2
          )
        )
      )
    )
  }));

  if (hoja["!ref"]) {
    hoja["!autofilter"] = {
      ref: hoja["!ref"]
    };
  }

  XLSX.utils.book_append_sheet(
    libro,
    hoja,
    nombre.substring(0, 31)
  );
};

const descargarExcel = (
  filas,
  nombre,
  hoja = "Datos"
) => {
  if (!filas?.length) {
    window.alert(
      "No hay registros para exportar."
    );

    return;
  }

  const libro = XLSX.utils.book_new();

  agregarHoja(
    libro,
    hoja,
    filas
  );

  XLSX.writeFile(
    libro,
    nombre
  );
};

const filasSituacionCierre = datos => {
  if (!datos) return [];

  if (Array.isArray(datos)) {
    return datos;
  }

  if (
    Array.isArray(
      datos.porDependencia
    )
  ) {
    return datos.porDependencia;
  }

  if (
    Array.isArray(
      datos.detalle
    )
  ) {
    return datos.detalle;
  }

  return Object.entries(datos).map(
    ([indicador, valor]) => ({
      Indicador: indicador,

      Valor:
        typeof valor === "object"
          ? JSON.stringify(valor)
          : valor
    })
  );
};

const esArchivado = e =>
  [
    e?.estado,
    e?.situacionActual
  ].some(v =>
    /\barchivad[oa]s?\b|\barchivo\b/i.test(
      String(v ?? "")
    )
  );

const soporteExpediente = e =>
  e?.tipoExpediente ||
  (
    e?.esDigital === true ||
    e?.esDigital === 1
      ? "Digital"
      : e?.esDigital === false ||
        e?.esDigital === 0
        ? "Físico"
        : "Sin especificar"
  );

const coincideSoporte = (
  e,
  tipo
) =>
  tipo === "all" ||
  soporteExpediente(e) === tipo;

const prepararExpedientes = expedientes =>
  expedientes.map(e => ({
    ID: e.id,

    Fecha:
      fechaAR(
        e.fecha
      ),

    Iniciador:
      e.iniciador ||
      "",

    Concepto:
      e.concepto ||
      "",

    Estado:
      e.estado ||
      "",

    Soporte:
      soporteExpediente(
        e
      ),

    DependenciaInicio:
      e.dependenciaInicio ||
      "",

    SedeInicio:
      e.sedeInicio ||
      "",

    DependenciaActual:
      e.dependenciaActual ||
      "",

    SedeActual:
      e.sedeActual ||
      "",

    SituacionActual:
      e.situacionActual ||
      "",

    Movimientos:
      e.movimientos ||
      0,

    Archivos:
      e.archivos ||
      0,

    UltimoEvento:
      fechaAR(
        e.ultimoEvento
      ),

    EnTransito:
      e.enTransito
        ? "Sí"
        : "No",

    AntecedentesNoConfiables:
      e.tieneAntecedentesNoConfiables
        ? "Sí"
        : "No"
  }));

// ======================================================
// AGRUPA TODAS LAS ADVERTENCIAS POR EXPEDIENTE
// ======================================================

const agruparAdvertenciasPorExpediente = lista => {
  const mapa = new Map();

  for (const item of lista || []) {
    const expedienteID =
      String(
        item?.expedienteID ??
        ""
      );

    if (!expedienteID) {
      continue;
    }

    if (!mapa.has(expedienteID)) {
      mapa.set(
        expedienteID,
        {
          expedienteID:
            item.expedienteID,

          tipoExpediente:
            item.tipoExpediente ||
            "Sin especificar",

          cantidadAdvertencias:
            0,

          movimientos:
            [],

          tipos:
            [],

          detalle:
            []
        }
      );
    }

    const grupo =
      mapa.get(
        expedienteID
      );

    if (
      (
        !grupo.tipoExpediente ||
        grupo.tipoExpediente ===
          "Sin especificar"
      ) &&
      item.tipoExpediente
    ) {
      grupo.tipoExpediente =
        item.tipoExpediente;
    }

    /*
      Soporta ambos formatos:

      1) backend viejo:
         una advertencia por registro.

      2) backend nuevo:
         un grupo por expediente
         con detalle[].
    */

    const detalles =
      Array.isArray(
        item.detalle
      )
        ? item.detalle
        : [item];

    for (
      const detalle
      of detalles
    ) {
      if (!detalle) {
        continue;
      }

      /*
        Armamos una clave para no
        duplicar una misma advertencia.
      */

      const claveDetalle = [
        detalle.movimientoID ??
          "",

        detalle.tipo ??
          "",

        detalle.mensaje ??
          detalle.motivo ??
          "",

        detalle.fecha ??
          detalle.fechaSalida ??
          "",

        detalle.fechaEntrada ??
          ""
      ].join("|");

      const yaExiste =
        grupo.detalle.some(
          d =>
            [
              d.movimientoID ??
                "",

              d.tipo ??
                "",

              d.mensaje ??
                d.motivo ??
                "",

              d.fecha ??
                d.fechaSalida ??
                "",

              d.fechaEntrada ??
                ""
            ].join("|") ===
            claveDetalle
        );

      if (!yaExiste) {
        grupo.detalle.push(
          detalle
        );
      }

      if (
        detalle.movimientoID !==
          null &&
        detalle.movimientoID !==
          undefined &&
        !grupo.movimientos.some(
          id =>
            String(id) ===
            String(
              detalle.movimientoID
            )
        )
      ) {
        grupo.movimientos.push(
          detalle.movimientoID
        );
      }

      if (
        detalle.tipo &&
        !grupo.tipos.includes(
          detalle.tipo
        )
      ) {
        grupo.tipos.push(
          detalle.tipo
        );
      }
    }
  }

  return Array.from(
    mapa.values()
  )
    .map(grupo => ({
      ...grupo,

      cantidadAdvertencias:
        grupo.detalle.length
    }))
    .sort(
      (a, b) =>
        b.cantidadAdvertencias -
        a.cantidadAdvertencias
    );
};

const prepararAdvertenciasAgrupadas =
  grupos =>
    (
      grupos || []
    ).map(
      grupo => ({
        Expediente:
          grupo.expedienteID,

        Soporte:
          grupo.tipoExpediente ||
          "Sin especificar",

        CantidadAdvertencias:
          grupo.cantidadAdvertencias ||
          0,

        Movimientos:
          (
            grupo.movimientos ||
            []
          ).join(", "),

        Tipos:
          (
            grupo.tipos ||
            []
          ).join(", "),

        Detalle:
          (
            grupo.detalle ||
            []
          )
            .map(
              item => {
                const movimiento =
                  item.movimientoID
                    ? `Mov. ${item.movimientoID}`
                    : "Expediente";

                return (
                  `${movimiento}: ${
                    item.mensaje ||
                    item.motivo ||
                    item.tipo ||
                    ""
                  }`
                );
              }
            )
            .join(" | ")
      })
    );

const exportarInformeCompleto = (
  informe,
  opciones = {}
) => {
  const {
    mostrarArchivados =
      false,

    tipoSoporte =
      "all"
  } = opciones;

  if (!informe) {
    return;
  }

  const expVisibles =
    (
      informe?.expedientes ||
      []
    ).filter(
      e =>
        (
          mostrarArchivados ||
          !esArchivado(e)
        ) &&
        coincideSoporte(
          e,
          tipoSoporte
        )
    );

  const idsVisibles =
    new Set(
      expVisibles.map(
        e =>
          String(
            e.id
          )
      )
    );

  const filtrarRegistros =
    filas =>
      (
        filas || []
      ).filter(
        f =>
          idsVisibles.has(
            String(
              f.expedienteID ??
              f.id
            )
          )
      );

  const libro =
    XLSX.utils.book_new();

  const r =
    informe.resumen ||
    {};

  const m =
    informe.metricasConfiables ||
    {};

  const c =
    informe.calidadDatos ||
    {};

  const advertenciasAgrupadasExcel =
    agruparAdvertenciasPorExpediente(
      informe
        ?.advertencias
        ?.detalle ||
      []
    );

  agregarHoja(
    libro,
    "Resumen",
    [
      {
        Indicador:
          "Expedientes",

        Valor:
          r.expedientes
      },

      {
        Indicador:
          "Iniciados en el período",

        Valor:
          r.iniciadosPeriodo
      },

      {
        Indicador:
          "Con actividad",

        Valor:
          r.conActividadPeriodo
      },

      {
        Indicador:
          "Salidas",

        Valor:
          r.movimientosSalida
      },

      {
        Indicador:
          "Recepciones",

        Valor:
          r.movimientosEntrada
      },

      {
        Indicador:
          "Recepciones físicas inferidas",

        Valor:
          r.recepcionesFisicasInferidas
      },

      {
        Indicador:
          "Archivos digitales",

        Valor:
          r.archivos
      },

      {
        Indicador:
          "Con archivos",

        Valor:
          r.conArchivos
      },

      {
        Indicador:
          "En dependencia",

        Valor:
          r.enDependencia
      },

      {
        Indicador:
          "En tránsito",

        Valor:
          r.enTransito
      },

      {
        Indicador:
          "Archivados",

        Valor:
          r.archivados
      },

      {
        Indicador:
          "Anulados",

        Valor:
          r.anulados
      },

      {
        Indicador:
          "Ubicación indeterminada",

        Valor:
          r.ubicacionIndeterminada
      },

      {
        Indicador:
          "Permanencia promedio total",

        Valor:
          r.promedioPermanenciaFinalizadaDias
      },

      {
        Indicador:
          "Permanencia promedio del período",

        Valor:
          r.promedioPermanenciaPeriodoDias
      },

      {
        Indicador:
          "Tránsito promedio total",

        Valor:
          r.promedioTransitoDias
      },

      {
        Indicador:
          "Permanencia confiable total",

        Valor:
          m.promedioPermanenciaDias
      },

      {
        Indicador:
          "Permanencia confiable del período",

        Valor:
          m.promedioPermanenciaPeriodoDias
      },

      {
        Indicador:
          "Tránsito confiable total",

        Valor:
          m.promedioTransitoDias
      },

      {
        Indicador:
          "Expedientes con advertencias",

        Valor:
          advertenciasAgrupadasExcel.length
      },

      {
        Indicador:
          "Eventos de advertencia",

        Valor:
          advertenciasAgrupadasExcel.reduce(
            (
              total,
              grupo
            ) =>
              total +
              (
                grupo
                  .cantidadAdvertencias ||
                0
              ),
            0
          )
      },

      {
        Indicador:
          "Inconsistencias",

        Valor:
          informe
            .inconsistencias
            ?.cantidad
      },

      {
        Indicador:
          "Fecha desde",

        Valor:
          informe
            .filtros
            ?.fechaDesde
      },

      {
        Indicador:
          "Fecha hasta",

        Valor:
          informe
            .filtros
            ?.fechaHasta
      },

      {
        Indicador:
          "Fecha de confiabilidad",

        Valor:
          informe
            .fechaConfiabilidad
      }
    ]
  );

  if (Array.isArray(informe.porDependenciaJerarquica) && informe.porDependenciaJerarquica.length) {
    agregarHoja(libro, "Jerarquia completa", informe.porDependenciaJerarquica.map(({dependenciasIncluidas, ...fila}) => ({...fila, dependenciasIncluidas:(dependenciasIncluidas||[]).join(", ")})));
  }
  if (Array.isArray(informe.porDependenciaAgrupada) && informe.porDependenciaAgrupada.length) {
    agregarHoja(libro, "Dependencias agrupadas", informe.porDependenciaAgrupada.map(({ dependenciasIncluidas, ...fila }) => ({ ...fila, dependenciasIncluidas: (dependenciasIncluidas || []).join(", ") })));
  }

  if (
    informe.situacionCierre
  ) {
    agregarHoja(
      libro,

      "Situacion al cierre",

      filasSituacionCierre(
        informe
          .situacionCierre
      )
    );
  }

  agregarHoja(
    libro,
    "Dependencias",
    informe
      .porDependencia ||
    []
  );

  agregarHoja(
    libro,
    "Sedes",
    informe
      .porSede ||
    []
  );

  agregarHoja(
    libro,
    "Evolucion",
    informe
      .evolucionMensual ||
    []
  );

  agregarHoja(
    libro,
    "Permanencias",
    filtrarRegistros(
      informe.permanencias
    )
  );

  agregarHoja(
    libro,
    "Transitos",
    filtrarRegistros(
      informe.transitos
    )
  );

  agregarHoja(
    libro,
    "Demorados",
    filtrarRegistros(
      informe.demorados
    )
  );

  agregarHoja(
    libro,

    "Tipos de archivos",

    informe
      .tiposArchivo ||
    []
  );

  agregarHoja(
    libro,

    "Calidad de datos",

    [
      {
        Indicador:
          "Antecedentes anteriores a 2025",

        Valor:
          c.movimientosAnteriores2025
      },

      {
        Indicador:
          "Expedientes con antecedentes",

        Valor:
          c.expedientesConAntecedentesNoConfiables
      },

      {
        Indicador:
          "Fechas invertidas",

        Valor:
          c.movimientosConFechasInvertidas
      },

      {
        Indicador:
          "Inversiones horarias menores",

        Valor:
          c.inversionesHorariasMenores
      },

      {
        Indicador:
          "Movimientos sin recepción",

        Valor:
          c.movimientosSinRecepcion
      },

      {
        Indicador:
          "Recepciones físicas inferidas",

        Valor:
          c.recepcionesFisicasInferidas
      },

      {
        Indicador:
          "Superposiciones",

        Valor:
          c.superposiciones
      },

      {
        Indicador:
          "Permanencias históricas advertidas",

        Valor:
          c.permanenciasHistoricasAdvertidas
      },

      {
        Indicador:
          "Tránsitos históricos advertidos",

        Valor:
          c.transitosHistoricosAdvertidos
      }
    ]
  );

  if (
    informe.expedientes
  ) {
    agregarHoja(
      libro,

      "Expedientes",

      prepararExpedientes(
        expVisibles
      )
    );
  }

  if (
    informe
      .advertencias
      ?.detalle
      ?.length
  ) {
    const advertenciasVisibles =
      advertenciasAgrupadasExcel.filter(
        grupo =>
          !informe.expedientes ||
          idsVisibles.has(
            String(
              grupo
                .expedienteID
            )
          )
      );

    agregarHoja(
      libro,

      "Advertencias",

      prepararAdvertenciasAgrupadas(
        advertenciasVisibles
      )
    );
  }

  if (
    informe
      .inconsistencias
      ?.detalle
      ?.length
  ) {
    agregarHoja(
      libro,

      "Inconsistencias",

      filtrarRegistros(
        informe
          .inconsistencias
          .detalle
      )
    );
  }

  const nombre =
    `SIGEDI_Informe_Expedientes_${informe.filtros?.fechaDesde || "inicio"}_${informe.filtros?.fechaHasta || "actual"}.xlsx`;

  XLSX.writeFile(
    libro,
    nombre
  );
};

const Indicador = ({
  titulo,
  valor,
  detalle,
  color = "#164b7e"
}) => (
  <div
    style={{
      ...estilos.tarjeta,

      marginBottom:
        0,

      borderTop:
        `4px solid ${color}`
    }}
  >
    <div
      style={{
        fontSize:
          12,

        color:
          "#64748b",

        fontWeight:
          600,

        marginBottom:
          8
      }}
    >
      {titulo}
    </div>

    <div
      style={{
        fontSize:
          28,

        fontWeight:
          700,

        color,

        overflowWrap:
          "anywhere"
      }}
    >
      {valor}
    </div>

    {detalle && (
      <div
        style={{
          fontSize:
            11,

          color:
            "#64748b",

          marginTop:
            7
        }}
      >
        {detalle}
      </div>
    )}
  </div>
);

const EnlaceExpediente = ({
  id
}) => {
  if (
    id === null ||
    id === undefined ||
    id === ""
  ) {
    return "—";
  }

  return (
    <a
      href={
        `/consultaExpediente?nroExpediente=${encodeURIComponent(id)}`
      }

      target="_blank"

      rel="noopener noreferrer"

      title="Abrir expediente en una pestaña nueva"

      style={{
        color:
          "#164b7e",

        fontWeight:
          700,

        textDecoration:
          "underline",

        cursor:
          "pointer"
      }}
    >
      {id}
    </a>
  );
};

const Tabla = ({
  columnas,
  filas,
  vacio =
    "No hay registros."
}) => (
  <div
    style={{
      overflowX:
        "auto"
    }}
  >
    <table
      style={{
        width:
          "100%",

        borderCollapse:
          "collapse",

        fontSize:
          13
      }}
    >
      <thead>
        <tr>
          {
            columnas.map(
              col => (
                <th
                  key={
                    col.key
                  }

                  style={
                    estilos.th
                  }
                >
                  {
                    col.nombre
                  }
                </th>
              )
            )
          }
        </tr>
      </thead>

      <tbody>
        {
          !filas?.length
            ? (
                <tr>
                  <td
                    colSpan={
                      columnas.length
                    }

                    style={
                      estilos.td
                    }
                  >
                    {vacio}
                  </td>
                </tr>
              )
            : filas.map(
                (
                  fila,
                  index
                ) => (
                  <tr
                    key={
                      `${fila.id ?? fila.expedienteID ?? "fila"}-${index}`
                    }
                  >
                    {
                      columnas.map(
                        col => (
                          <td
                            key={
                              col.key
                            }

                            style={
                              estilos.td
                            }
                          >
                            {
                              col.render
                                ? col.render(
                                    fila
                                  )
                                : fila[
                                    col.key
                                  ] ??
                                  "—"
                            }
                          </td>
                        )
                      )
                    }
                  </tr>
                )
              )
        }
      </tbody>
    </table>
  </div>
);

const GraficoBarras = ({
  datos,
  campo,
  etiqueta
}) => {
  const maximo =
    Math.max(
      1,

      ...datos.map(
        d =>
          Number(
            d[campo] ||
            0
          )
      )
    );

  if (!datos.length) {
    return (
      <p
        style={{
          color:
            "#64748b"
        }}
      >
        Sin datos para graficar.
      </p>
    );
  }

  return (
    <div
      style={{
        display:
          "grid",

        gap:
          12
      }}
    >
      {
        datos.map(
          (
            d,
            index
          ) => {
            const valor =
              Number(
                d[campo] ||
                0
              );

            return (
              <div
                key={
                  `${d.id ?? d.mes}-${index}`
                }
              >
                <div
                  style={{
                    display:
                      "flex",

                    justifyContent:
                      "space-between",

                    gap:
                      10,

                    fontSize:
                      12,

                    marginBottom:
                      4
                  }}
                >
                  <span>
                    {
                      d.nombre ??
                      d.mes
                    }
                  </span>

                  <strong>
                    {
                      numero(
                        valor
                      )
                    }
                  </strong>
                </div>

                <div
                  style={{
                    background:
                      "#e9eff6",

                    height:
                      11,

                    borderRadius:
                      6,

                    overflow:
                      "hidden"
                  }}
                >
                  <div
                    style={{
                      height:
                        "100%",

                      width:
                        `${(valor / maximo) * 100}%`,

                      background:
                        "#2674b8",

                      borderRadius:
                        6
                    }}
                  />
                </div>
              </div>
            );
          }
        )
      }

      <small
        style={{
          color:
            "#64748b"
        }}
      >
        {etiqueta}
      </small>
    </div>
  );
};

// Tabla desplegable: métricas acumuladas por dirección y desglose por hijas.
const TablaDependenciasJerarquica = ({ filas = [], detalle = [], expandirInicial = false }) => {
  const [abiertos, setAbiertos] = useState(new Set(["1"]));
  const [soloActividad, setSoloActividad] = useState(true);
  const datos = useMemo(() => {
    const mapa = new Map(filas.map(f => [String(f.id), { ...f, hijos: [] }]));
    const raices = [];
    for (const nodo of mapa.values()) {
      const padre = nodo.dependenciaPadreId == null ? null : mapa.get(String(nodo.dependenciaPadreId));
      if (padre && padre !== nodo) padre.hijos.push(nodo);
      else raices.push(nodo);
    }
    const ordenar = nodos => {
      nodos.sort((a,b) => Number(b.expedientesUnicos || 0) - Number(a.expedientesUnicos || 0));
      nodos.forEach(n => ordenar(n.hijos));
    };
    ordenar(raices);
    return { mapa, raices };
  }, [filas]);
  const directos = useMemo(() => new Map(detalle.map(f => [String(f.id), f])), [detalle]);
  const desplegar = id => setAbiertos(prev => {
    const nuevo = new Set(prev);
    if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
    return nuevo;
  });
  const expandirTodo = () => setAbiertos(new Set(datos.mapa.keys()));
  const contraerTodo = () => setAbiertos(new Set());
  const filasVisibles = [];
  const visitar = (nodos, nivel = 0, camino = new Set()) => {
    for (const nodo of nodos) {
      const id = String(nodo.id);
      if (camino.has(id)) continue;
      if (soloActividad && Number(nodo.expedientesUnicos || 0) === 0 && !nodo.hijos.length) continue;
      filasVisibles.push({ ...nodo, profundidad: nivel });
      if (abiertos.has(id) || expandirInicial) visitar(nodo.hijos, nivel + 1, new Set([...camino, id]));
    }
  };
  visitar(datos.raices);
  const columnas = [
    ['expedientesUnicos','Expedientes'],['recibidos','Recibidos'],['enviados','Enviados'],
    ['archivos','Archivos'],['permanenciaPromedioConfiableDias','Permanencia (días)'],
    ['transitoPromedioConfiableDias','Tránsito (días)'],['pendientesActuales','Pendientes'],
    ['enTransitoActuales','En tránsito']
  ];
  return <div>
    <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:14}}>
      <button type="button" style={estilos.boton} onClick={expandirTodo}>Expandir todas</button>
      <button type="button" style={estilos.boton} onClick={contraerTodo}>Contraer todas</button>
      <label style={{fontSize:13}}><input type="checkbox" checked={soloActividad} onChange={e=>setSoloActividad(e.target.checked)}/> Ocultar destinos sin actividad</label>
      <small style={{color:'#64748b'}}>Las filas superiores incluyen sus subordinadas; las hijas son un desglose, no se suman nuevamente.</small>
    </div>
    <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
      <thead><tr><th style={estilos.th}>Dependencia / Dirección</th>{columnas.map(([k,t])=><th key={k} style={estilos.th}>{t}</th>)}</tr></thead>
      <tbody>{filasVisibles.map(nodo=>{
        const id=String(nodo.id), hijos=nodo.hijos.length, abierto=abiertos.has(id) || expandirInicial;
        const directo=directos.get(id);
        return <tr key={id} style={{background:nodo.profundidad===0?'#f1f6fc':'#fff'}}>
          <td style={{...estilos.td,minWidth:260,paddingLeft:12+nodo.profundidad*23}}>
            {hijos>0 ? <button type="button" aria-label={abierto?'Contraer':'Expandir'} onClick={()=>desplegar(id)} style={{border:0,background:'transparent',cursor:'pointer',color:'#164b7e',fontWeight:700,marginRight:7}}>{abierto?'▼':'▶'}</button> : <span style={{display:'inline-block',width:23}}/>}
            <strong style={{fontWeight:hijos?700:400}}>{nodo.nombre}</strong>
            {directo && <span title="Expedientes propios de este destino" style={{fontSize:11,color:'#64748b',marginLeft:8}}>({numero(directo.expedientesUnicos)} propios)</span>}
          </td>
          {columnas.map(([k])=><td key={k} style={estilos.td}>{k.includes('Dias')?decimal(nodo[k]):numero(nodo[k])}</td>)}
        </tr>;
      })}</tbody>
    </table></div>
  </div>;
};

const SelectorDependencia = ({
  destinos,
  valor,
  onChange
}) => {
  const [
    busqueda,
    setBusqueda
  ] = useState("");

  const [
    abierto,
    setAbierto
  ] = useState(false);

  const filtrados =
    useMemo(
      () => {
        const texto =
          busqueda
            .trim()
            .toLowerCase();

        return destinos.filter(
          d =>
            String(
              d.descripcion ||
              ""
            )
              .toLowerCase()
              .includes(
                texto
              ) ||
            String(
              d.id
            ).includes(
              texto
            )
        );
      },

      [
        destinos,
        busqueda
      ]
    );

  const seleccionado =
    destinos.find(
      d =>
        String(
          d.id
        ) ===
        String(
          valor
        )
    );

  return (
    <div
      style={{
        position:
          "relative"
      }}
    >
      <small>
        Dependencia
      </small>

      <button
        type="button"

        onClick={
          () =>
            setAbierto(
              !abierto
            )
        }

        style={{
          ...estilos.input,

          textAlign:
            "left",

          cursor:
            "pointer",

          minHeight:
            39
        }}
      >
        {
          valor ===
          "all"
            ? "Todas las dependencias"
            : seleccionado
                ?.descripcion ||
              `Dependencia ${valor}`
        }

        <span
          style={{
            float:
              "right"
          }}
        >
          ▼
        </span>
      </button>

      {abierto && (
        <div
          style={{
            position:
              "absolute",

            top:
              "100%",

            left:
              0,

            right:
              0,

            zIndex:
              100,

            background:
              "#fff",

            border:
              "1px solid #d5dce5",

            borderRadius:
              8,

            boxShadow:
              "0 6px 20px rgba(0,0,0,.15)",

            padding:
              8
          }}
        >
          <input
            type="text"

            style={
              estilos.input
            }

            placeholder="Buscar dependencia..."

            value={
              busqueda
            }

            onChange={
              e =>
                setBusqueda(
                  e.target.value
                )
            }

            autoFocus
          />

          <div
            style={{
              maxHeight:
                230,

              overflowY:
                "auto",

              marginTop:
                7
            }}
          >
            <div
              onClick={
                () => {
                  onChange(
                    "all"
                  );

                  setAbierto(
                    false
                  );

                  setBusqueda(
                    ""
                  );
                }
              }

              style={{
                padding:
                  9,

                cursor:
                  "pointer",

                background:
                  valor ===
                  "all"
                    ? "#e8f1fc"
                    : "#fff"
              }}
            >
              Todas las dependencias
            </div>

            {
              filtrados.map(
                d => (
                  <div
                    key={
                      d.id
                    }

                    onClick={
                      () => {
                        onChange(
                          String(
                            d.id
                          )
                        );

                        setAbierto(
                          false
                        );

                        setBusqueda(
                          ""
                        );
                      }
                    }

                    style={{
                      padding:
                        9,

                      cursor:
                        "pointer",

                      borderTop:
                        "1px solid #edf1f5",

                      background:
                        String(
                          valor
                        ) ===
                        String(
                          d.id
                        )
                          ? "#e8f1fc"
                          : "#fff",

                      fontSize:
                        13
                    }}
                  >
                    {
                      d.descripcion
                    }
                  </div>
                )
              )
            }

            {!filtrados.length && (
              <div
                style={{
                  padding:
                    10,

                  color:
                    "#94a3b8"
                }}
              >
                Sin resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function InformeEstadisticoExpedientes({
  apiUrl =
    "/estadisticas/informeGeneral",

  token =
    null
}) {
  const hoy =
    useMemo(
      () =>
        fechaLocal(
          new Date()
        ),
      []
    );

  const primerDia =
    `${hoy.substring(0, 7)}-01`;

  const destinosOriginales =
    devuelveDependenciaConstultada();

  const sedesOriginales =
    devuelveSede();

  const destinos =
    Array.isArray(
      destinosOriginales
    )
      ? destinosOriginales
      : [];

  const sedes =
    Array.isArray(
      sedesOriginales
    )
      ? sedesOriginales
      : [];

  const [
    filtros,
    setFiltros
  ] = useState({
    fechaDesde:
      primerDia,

    fechaHasta:
      hoy,

    dependencia:
      "all",

    sede:
      "all",

    estado:
      "all",

    modo:
      "actividad",

    incluirDetalle:
      true,
    modoAgrupacion: "individual",
    nivelAgrupacion: 2,
    dependenciasSeleccionadas: [],
    incluirSubordinadas: true
  });

  const [
    informe,
    setInforme
  ] = useState(null);

  const [mostrarAgrupados, setMostrarAgrupados] = useState(true);
  const [busquedaSeleccion, setBusquedaSeleccion] = useState("");

  const [
    cargando,
    setCargando
  ] = useState(false);

  const [
    error,
    setError
  ] = useState("");

  const [
    pestana,
    setPestana
  ] = useState("general");

  const [
    busqueda,
    setBusqueda
  ] = useState("");

  const [
    tipoAdvertencia,
    setTipoAdvertencia
  ] = useState("all");

  const [
    expedienteSeleccionado,
    setExpedienteSeleccionado
  ] = useState(null);

  const [
    limite,
    setLimite
  ] = useState(50);

  const [
    mostrarArchivados,
    setMostrarArchivados
  ] = useState(false);

  const [
    tipoSoporte,
    setTipoSoporte
  ] = useState("all");

  const navitate =
    useNavigate();

  const cambiarFiltro = (
    campo,
    valor
  ) => {
    setFiltros(
      prev => ({
        ...prev,

        [campo]:
          valor
      })
    );
  };

  const consultar =
    useCallback(
      async () => {
        if (filtros.modoAgrupacion === "personalizado" && !filtros.dependenciasSeleccionadas.length) {
          setError("Seleccioná al menos una dependencia para la agrupación personalizada.");
          return;
        }
        if (
          !filtros.fechaDesde ||
          !filtros.fechaHasta ||
          filtros.fechaDesde >
            filtros.fechaHasta
        ) {
          setError(
            "Verificá el rango de fechas seleccionado."
          );

          return;
        }

        setCargando(
          true
        );

        setError(
          ""
        );

        setInforme(
          null
        );

        setExpedienteSeleccionado(
          null
        );

        try {
          const respuesta =
            await axios.post(
              apiUrl,

              {
                ...filtros,

                incluirArchivados:
                  mostrarArchivados,

                tipoSoporte
              },

              {
                headers:
                  token
                    ? {
                        Authorization:
                          `Bearer ${token}`
                      }
                    : {}
              }
            );

          if (
            !respuesta.data
              ?.ok
          ) {
            throw new Error(
              respuesta.data
                ?.message ||
              "No se pudo generar el informe."
            );
          }

          if (
            filtros.modo ===
              "cierre" &&
            !respuesta.data
              ?.situacionCierre
          ) {
            throw new Error(
              "El backend todavía no devuelve situacionCierre. Debés implementar el cálculo histórico al cierre del período en el controlador para utilizar este modo."
            );
          }

          setInforme(
            respuesta.data
          );

          setPestana(
            filtros.modo ===
              "cierre"
              ? "cierre"
              : "general"
          );

        } catch (err) {
          setError(
            err.response
              ?.data
              ?.message ||
            err.message ||
            "Error al consultar las estadísticas."
          );

        } finally {
          setCargando(
            false
          );
        }
      },

      [
        apiUrl,
        token,
        filtros,
        mostrarArchivados,
        tipoSoporte
      ]
    );

  const resumen =
    informe?.resumen ||
    {};

  const confiables =
    informe?.metricasConfiables ||
    {};

  const calidad =
    informe?.calidadDatos ||
    {};

  const dependencias =
    informe?.porDependencia ||
    [];

  const dependenciasAgrupadas = informe?.porDependenciaAgrupada || [];
  const dependenciasJerarquicas = informe?.porDependenciaJerarquica || [];
  const [vistaDependencias, setVistaDependencias] = useState("arbol");
  const usandoAgrupados = filtros.modoAgrupacion !== "individual" && mostrarAgrupados;
  const dependenciasGrafico = usandoAgrupados ? dependenciasAgrupadas : dependencias;
  const nivelesDisponibles = Object.values(informe?.nivelesDependencias || {}).map(Number).filter(Number.isFinite);
  const maximoNivel = Math.max(1, ...nivelesDisponibles, ...destinos.map(d => Number(d.nivel || 0)).filter(Number.isFinite));

  const porSede =
    informe?.porSede ||
    [];

  const evolucion =
    informe?.evolucionMensual ||
    [];

  const expedientes =
    informe?.expedientes ||
    [];

  const advertenciasRaw =
    informe
      ?.advertencias
      ?.detalle ||
    [];

  const inconsistencias =
    informe
      ?.inconsistencias
      ?.detalle ||
    [];

  /*
    ACÁ ESTÁ LA CORRECCIÓN PRINCIPAL:
    aunque el backend mande varias filas
    del mismo expediente, React las
    vuelve a consolidar.
  */

  const advertenciasAgrupadas =
    useMemo(
      () =>
        agruparAdvertenciasPorExpediente(
          advertenciasRaw
        ),

      [
        advertenciasRaw
      ]
    );

  const sedesDisponibles =
    useMemo(
      () => [
        {
          id:
            "all",

          nombre:
            "Todas las sedes"
        },

        ...sedes.map(
          s => ({
            id:
              String(
                s.id
              ),

            nombre:
              s.descripcion ||
              s.nombre ||
              `Sede ${s.id}`
          })
        )
      ],

      [
        sedes
      ]
    );

  const expedientesVisibles =
    useMemo(
      () =>
        expedientes.filter(
          e =>
            (
              mostrarArchivados ||
              !esArchivado(e)
            ) &&
            coincideSoporte(
              e,
              tipoSoporte
            )
        ),

      [
        expedientes,
        mostrarArchivados,
        tipoSoporte
      ]
    );

  const idsVisibles =
    useMemo(
      () =>
        new Set(
          expedientesVisibles.map(
            e =>
              String(
                e.id
              )
          )
        ),

      [
        expedientesVisibles
      ]
    );

  const expedienteEsVisible =
    id =>
      !informe?.expedientes ||
      idsVisibles.has(
        String(id)
      );

  const expedientesFiltrados =
    useMemo(
      () => {
        const texto =
          busqueda
            .toLowerCase()
            .trim();

        return expedientesVisibles.filter(
          e =>
            !texto ||
            [
              e.id,
              e.iniciador,
              e.concepto,
              e.estado,

              soporteExpediente(
                e
              ),

              e.dependenciaActual,
              e.dependenciaInicio,
              e.sedeActual
            ].some(
              v =>
                String(
                  v ?? ""
                )
                  .toLowerCase()
                  .includes(
                    texto
                  )
            )
        );
      },

      [
        expedientesVisibles,
        busqueda
      ]
    );

  const demoradosFiltrados =
    useMemo(
      () =>
        (
          informe
            ?.demorados ||
          []
        ).filter(
          d =>
            expedienteEsVisible(
              d.expedienteID
            ) &&
            coincideSoporte(
              d,
              tipoSoporte
            )
        ),

      [
        informe,
        idsVisibles,
        tipoSoporte
      ]
    );

  const advertenciasFiltradas =
    useMemo(
      () => {
        const texto =
          busqueda
            .toLowerCase()
            .trim();

        return advertenciasAgrupadas.filter(
          grupo => {
            if (
              !expedienteEsVisible(
                grupo.expedienteID
              )
            ) {
              return false;
            }

            if (
              tipoAdvertencia !==
                "all" &&
              !(
                grupo.tipos ||
                []
              ).includes(
                tipoAdvertencia
              )
            ) {
              return false;
            }

            if (!texto) {
              return true;
            }

            const valoresBusqueda = [
              grupo.expedienteID,
              grupo.tipoExpediente,

              ...(
                grupo.movimientos ||
                []
              ),

              ...(
                grupo.tipos ||
                []
              ),

              ...(
                grupo.detalle ||
                []
              ).flatMap(
                item => [
                  item.movimientoID,
                  item.tipo,
                  item.mensaje,
                  item.motivo
                ]
              )
            ];

            return valoresBusqueda.some(
              valor =>
                String(
                  valor ??
                  ""
                )
                  .toLowerCase()
                  .includes(
                    texto
                  )
            );
          }
        );
      },

      [
        advertenciasAgrupadas,
        idsVisibles,
        tipoAdvertencia,
        busqueda
      ]
    );

  const inconsistenciasFiltradas =
    useMemo(
      () =>
        inconsistencias.filter(
          i =>
            expedienteEsVisible(
              i.expedienteID
            )
        ),

      [
        inconsistencias,
        idsVisibles
      ]
    );

  const tiposAdvertencia =
    Object.keys(
      informe
        ?.advertencias
        ?.porTipo ||
      {}
    );

  const pestanas = [
    [
      "general",
      "Resumen general"
    ],

    ...(
      informe?.situacionCierre
        ? [
            [
              "cierre",
              "Situación al cierre"
            ]
          ]
        : []
    ),

    [
      "dependencias",
      "Dependencias"
    ],

    ...(dependenciasAgrupadas.length ? [["agrupadas", "Agrupación jerárquica"]] : []),

    [
      "sedes",
      "Sedes"
    ],

    [
      "evolucion",
      "Evolución"
    ],

    [
      "demoras",
      "Demoras"
    ],

    [
      "expedientes",
      "Expedientes"
    ],

    [
      "advertencias",
      "Advertencias"
    ],

    [
      "inconsistencias",
      "Inconsistencias"
    ]
  ];

  const titulo =
    texto => (
      <h3
        style={{
          margin:
            "0 0 16px",

          color:
            "#164b7e"
        }}
      >
        {texto}
      </h3>
    );

  return (
    <div
      style={
        estilos.contenedor
      }
    >
      <div
        style={{
          background:
            "linear-gradient(110deg,#103b65,#216b9f)",

          color:
            "#fff",

          padding:
            23,

          borderRadius:
            12,

          marginBottom:
            18
        }}
      >
        <button
          className="btn btn-warning"

          onClick={
            () =>
              navitate(
                "/consultaExpediente"
              )
          }
        >
          Volver
        </button>

        <h2
          style={{
            margin:
              "0 0 7px"
          }}
        >
          Informe Estadístico de Expedientes
        </h2>

        <div
          style={{
            opacity:
              .85,

            fontSize:
              13
          }}
        >
          SIGEDI · Instituto de Seguridad Pública de Santa Fe
        </div>
      </div>

      <div
        style={
          estilos.tarjeta
        }
      >
        {
          titulo(
            "Filtros del informe"
          )
        }

        <div
          style={{
            display:
              "grid",

            gridTemplateColumns:
              "repeat(auto-fit,minmax(180px,1fr))",

            gap:
              12
          }}
        >
          <label>
            <small>
              Desde
            </small>

            <input
              type="date"

              style={
                estilos.input
              }

              value={
                filtros.fechaDesde
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "fechaDesde",
                    e.target.value
                  )
              }
            />
          </label>

          <label>
            <small>
              Hasta
            </small>

            <input
              type="date"

              style={
                estilos.input
              }

              value={
                filtros.fechaHasta
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "fechaHasta",
                    e.target.value
                  )
              }
            />
          </label>

          <SelectorDependencia
            destinos={
              destinos
            }

            valor={
              filtros.dependencia
            }

            onChange={
              valor =>
                cambiarFiltro(
                  "dependencia",
                  valor
                )
            }
          />

          <label>
            <small>
              Sede
            </small>

            <select
              style={
                estilos.input
              }

              value={
                filtros.sede
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "sede",
                    e.target.value
                  )
              }
            >
              {
                sedesDisponibles.map(
                  s => (
                    <option
                      key={
                        s.id
                      }

                      value={
                        s.id
                      }
                    >
                      {
                        s.nombre
                      }
                    </option>
                  )
                )
              }
            </select>
          </label>

          <label>
            <small>
              Estado
            </small>

            <select
              style={
                estilos.input
              }

              value={
                filtros.estado
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "estado",
                    e.target.value
                  )
              }
            >
              <option value="all">
                Todos
              </option>

              <option value="En el ISeP">
                En el ISeP
              </option>

              <option value="Archivo">
                Archivo
              </option>

              <option value="Anulado">
                Anulado
              </option>
            </select>
          </label>

          <label>
            <small>
              Tipo de consulta
            </small>

            <select
              style={
                estilos.input
              }

              value={
                filtros.modo
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "modo",
                    e.target.value
                  )
              }
            >
              <option value="actividad">
                Actividad del período
              </option>

              <option value="creacion">
                Expedientes iniciados
              </option>

              <option value="cierre">
                Situación al cierre del período
              </option>
            </select>
          </label>
        </div>

        <div style={{ marginTop: 18, padding: 15, background: "#f0f5fb", border: "1px solid #d8e4f0", borderRadius: 10 }}>
          <strong style={{ color: "#164b7e" }}>Agrupación institucional</strong>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12, marginTop: 12 }}>
            <label><small>Tipo de agrupación</small>
              <select style={estilos.input} value={filtros.modoAgrupacion} onChange={e => cambiarFiltro("modoAgrupacion", e.target.value)}>
                <option value="individual">Individual (original)</option>
                <option value="jerarquico">Por nivel jerárquico</option>
                <option value="personalizado">Dependencias seleccionadas</option>
              </select>
            </label>
            {filtros.modoAgrupacion === "jerarquico" && <label><small>Nivel a agrupar</small>
              <select style={estilos.input} value={filtros.nivelAgrupacion} onChange={e => cambiarFiltro("nivelAgrupacion", Number(e.target.value))}>
                {Array.from({ length: Math.max(6, maximoNivel) }, (_, i) => i + 1).map(n => <option key={n} value={n}>Nivel {n}</option>)}
              </select>
            </label>}
            {filtros.modoAgrupacion !== "individual" && <label style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 16 }}>
              <input type="checkbox" checked={filtros.incluirSubordinadas} onChange={e => cambiarFiltro("incluirSubordinadas", e.target.checked)} />
              Incluir dependencias subordinadas
            </label>}
          </div>
          {filtros.modoAgrupacion === "personalizado" && <div style={{ marginTop: 12 }}>
            <input style={estilos.input} placeholder="Buscar dirección o dependencia por nombre o ID..." value={busquedaSeleccion} onChange={e => setBusquedaSeleccion(e.target.value)} />
            <div style={{ maxHeight: 210, overflowY: "auto", background: "white", border: "1px solid #dce4ef", marginTop: 6, padding: 8, borderRadius: 8 }}>
              {destinos.filter(d => `${d.descripcion || ""} ${d.id}`.toLowerCase().includes(busquedaSeleccion.toLowerCase())).map(d => {
                const id = Number(d.id);
                const marcado = filtros.dependenciasSeleccionadas.some(x => Number(x) === id);
                return <label key={d.id} style={{ display: "flex", gap: 9, alignItems: "center", padding: 5, fontSize: 13 }}>
                  <input type="checkbox" checked={marcado} onChange={e => cambiarFiltro("dependenciasSeleccionadas", e.target.checked ? [...filtros.dependenciasSeleccionadas, id] : filtros.dependenciasSeleccionadas.filter(x => Number(x) !== id))} />
                  {d.descripcion || `Dependencia ${id}`} <small style={{ color: "#64748b" }}>(ID {id})</small>
                </label>;
              })}
            </div>
            <small>{filtros.dependenciasSeleccionadas.length} dependencias seleccionadas</small>
          </div>}
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 0 }}>En modo jerárquico se conserva el resumen global. En modo personalizado el backend limita el universo a las dependencias seleccionadas. El gráfico original sigue disponible.</p>
        </div>

        {
          filtros.modo ===
            "cierre" && (
            <p
              style={{
                fontSize:
                  12,

                color:
                  "#64748b",

                marginTop:
                  12
              }}
            >
              La fecha «Hasta» es el corte histórico. Se incluyen expedientes sin movimientos durante el período, siempre que el backend reconstruya su situación a esa fecha.
            </p>
          )
        }

        <div
          style={{
            display:
              "flex",

            alignItems:
              "center",

            gap:
              12,

            flexWrap:
              "wrap",

            marginTop:
              16
          }}
        >
          <label
            style={{
              display:
                "flex",

              alignItems:
                "center",

              gap:
                7,

              fontSize:
                13
            }}
          >
            <input
              type="checkbox"

              checked={
                mostrarArchivados
              }

              onChange={
                e =>
                  setMostrarArchivados(
                    e.target.checked
                  )
              }
            />

            Mostrar expedientes archivados
          </label>

          <label
            style={{
              fontSize:
                13
            }}
          >
            Soporte:{" "}

            <select
              value={
                tipoSoporte
              }

              onChange={
                e =>
                  setTipoSoporte(
                    e.target.value
                  )
              }

              style={{
                ...estilos.input,

                width:
                  "auto",

                minWidth:
                  160
              }}
            >
              <option value="all">
                Todos
              </option>

              <option value="Digital">
                Digital
              </option>

              <option value="Físico">
                Físico
              </option>

              <option value="Sin especificar">
                Sin especificar
              </option>
            </select>
          </label>

          <label
            style={{
              fontSize:
                13
            }}
          >
            <input
              type="checkbox"

              checked={
                filtros.incluirDetalle
              }

              onChange={
                e =>
                  cambiarFiltro(
                    "incluirDetalle",
                    e.target.checked
                  )
              }
            />

            {" "}
            Incluir detalle individual
          </label>

          <button
            type="button"

            style={{
              ...estilos.boton,

              opacity:
                cargando
                  ? .6
                  : 1
            }}

            onClick={
              consultar
            }

            disabled={
              cargando
            }
          >
            {
              cargando
                ? "Procesando..."
                : "Generar informe"
            }
          </button>

          {informe && (
            <button
              type="button"

              style={
                estilos.botonExcel
              }

              onClick={
                () =>
                  exportarInformeCompleto(
                    informe,
                    {
                      mostrarArchivados,
                      tipoSoporte
                    }
                  )
              }
            >
              Exportar informe completo a Excel
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            ...estilos.tarjeta,

            color:
              "#b42318",

            background:
              "#fff1f0",

            border:
              "1px solid #ffcbc7"
          }}
        >
          {error}
        </div>
      )}

      {!informe &&
        !cargando && (
        <div
          style={
            estilos.tarjeta
          }
        >
          Seleccioná las fechas y presioná{" "}
          <strong>
            Generar informe
          </strong>.
        </div>
      )}

      {informe && (
        <>
          <div
            style={{
              display:
                "flex",

              justifyContent:
                "space-between",

              flexWrap:
                "wrap",

              gap:
                8,

              marginBottom:
                14,

              fontSize:
                12,

              color:
                "#64748b"
            }}
          >
            <span>
              Generado:{" "}
              {
                fechaAR(
                  informe.generadoAt
                )
              }
            </span>

            <span>
              Datos confiables desde: 01/01/2025
            </span>
          </div>

          {filtros.modo !==
            "cierre" && (
            <div
              style={{
                display:
                  "grid",

                gridTemplateColumns:
                  "repeat(auto-fit,minmax(160px,1fr))",

                gap:
                  12,

                marginBottom:
                  18
              }}
            >
              <Indicador
                titulo="Expedientes"

                valor={
                  numero(
                    resumen.expedientes
                  )
                }

                detalle="Incluidos en la consulta"
              />

              <Indicador
                titulo="Iniciados"

                valor={
                  numero(
                    resumen.iniciadosPeriodo
                  )
                }

                detalle="Durante el período"

                color="#16805d"
              />

              <Indicador
                titulo="Salidas"

                valor={
                  numero(
                    resumen.movimientosSalida
                  )
                }
              />

              <Indicador
                titulo="Recepciones"

                valor={
                  numero(
                    resumen.movimientosEntrada
                  )
                }
              />

              <Indicador
                titulo="Recepciones físicas inferidas"

                valor={
                  numero(
                    resumen.recepcionesFisicasInferidas
                  )
                }

                detalle="Reconstruidas por una salida posterior"

                color="#9a6700"
              />

              <Indicador
                titulo="Archivos digitales"

                valor={
                  numero(
                    resumen.archivos
                  )
                }

                color="#7151a3"
              />

              <Indicador
                titulo="En dependencia"

                valor={
                  numero(
                    resumen.enDependencia
                  )
                }

                color="#16805d"
              />

              <Indicador
                titulo="En tránsito"

                valor={
                  numero(
                    resumen.enTransito
                  )
                }

                color="#c47b17"
              />

              <Indicador
                titulo="Archivados"

                valor={
                  numero(
                    resumen.archivados
                  )
                }
              />

              <Indicador
                titulo="Ubicación indeterminada"

                valor={
                  numero(
                    resumen.ubicacionIndeterminada
                  )
                }

                color="#c0392b"
              />

              <Indicador
                titulo="Expedientes con advertencias"

                valor={
                  numero(
                    advertenciasAgrupadas.length
                  )
                }

                color="#c47b17"
              />

              <Indicador
                titulo="Inconsistencias"

                valor={
                  numero(
                    informe
                      .inconsistencias
                      ?.cantidad
                  )
                }

                color="#c0392b"
              />

              <Indicador
                titulo="Expedientes con archivos"

                valor={
                  numero(
                    resumen.conArchivos
                  )
                }

                color="#7151a3"
              />
            </div>
          )}

          <div
            style={{
              display:
                "flex",

              flexWrap:
                "wrap",

              gap:
                7,

              marginBottom:
                16
            }}
          >
            {
              pestanas
                .filter(
                  ([id]) =>
                    filtros.modo !==
                      "cierre" ||
                    id ===
                      "cierre"
                )
                .map(
                  (
                    [
                      id,
                      nombre
                    ]
                  ) => (
                    <button
                      key={
                        id
                      }

                      type="button"

                      onClick={
                        () => {
                          setPestana(
                            id
                          );

                          setLimite(
                            50
                          );

                          setBusqueda(
                            ""
                          );
                        }
                      }

                      style={{
                        ...estilos.boton,

                        padding:
                          "9px 12px",

                        fontSize:
                          12,

                        background:
                          pestana ===
                          id
                            ? "#164b7e"
                            : "#e2eaf3",

                        color:
                          pestana ===
                          id
                            ? "#fff"
                            : "#254765"
                      }}
                    >
                      {
                        nombre
                      }
                    </button>
                  )
                )
            }
          </div>

          {pestana ===
            "general" && (
            <>
              <div
                style={{
                  display:
                    "grid",

                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(280px,1fr))",

                  gap:
                    14
                }}
              >
                <div
                  style={
                    estilos.tarjeta
                  }
                >
                  {
                    titulo(
                      "Tiempos de gestión"
                    )
                  }

                  <Tabla
                    columnas={[
                      {
                        key:
                          "concepto",

                        nombre:
                          "Indicador"
                      },

                      {
                        key:
                          "valor",

                        nombre:
                          "Días"
                      }
                    ]}

                    filas={[
                      {
                        concepto:
                          "Permanencia promedio total",

                        valor:
                          decimal(
                            resumen.promedioPermanenciaFinalizadaDias
                          )
                      },

                      {
                        concepto:
                          "Permanencia dentro del período",

                        valor:
                          decimal(
                            resumen.promedioPermanenciaPeriodoDias
                          )
                      },

                      {
                        concepto:
                          "Permanencia confiable total",

                        valor:
                          decimal(
                            confiables.promedioPermanenciaDias
                          )
                      },

                      {
                        concepto:
                          "Permanencia confiable del período",

                        valor:
                          decimal(
                            confiables.promedioPermanenciaPeriodoDias
                          )
                      },

                      {
                        concepto:
                          "Tránsito promedio total",

                        valor:
                          decimal(
                            resumen.promedioTransitoDias
                          )
                      },

                      {
                        concepto:
                          "Tránsito confiable total",

                        valor:
                          decimal(
                            confiables.promedioTransitoDias
                          )
                      }
                    ]}
                  />

                  <p
                    style={{
                      fontSize:
                        12,

                      color:
                        "#64748b"
                    }}
                  >
                    Las métricas confiables excluyen intervalos iniciados antes del 01/01/2025.
                  </p>
                </div>

                <div
                  style={
                    estilos.tarjeta
                  }
                >
                  {
                    titulo(
                      "Control de calidad"
                    )
                  }

                  <Tabla
                    columnas={[
                      {
                        key:
                          "concepto",

                        nombre:
                          "Control"
                      },

                      {
                        key:
                          "valor",

                        nombre:
                          "Cantidad"
                      }
                    ]}

                    filas={[
                      {
                        concepto:
                          "Movimientos anteriores a 2025",

                        valor:
                          numero(
                            calidad.movimientosAnteriores2025
                          )
                      },

                      {
                        concepto:
                          "Fechas invertidas",

                        valor:
                          numero(
                            calidad.movimientosConFechasInvertidas
                          )
                      },

                      {
                        concepto:
                          "Inversiones horarias menores",

                        valor:
                          numero(
                            calidad.inversionesHorariasMenores
                          )
                      },

                      {
                        concepto:
                          "Movimientos sin recepción",

                        valor:
                          numero(
                            calidad.movimientosSinRecepcion
                          )
                      },

                      {
                        concepto:
                          "Recepciones físicas inferidas",

                        valor:
                          numero(
                            calidad.recepcionesFisicasInferidas
                          )
                      },

                      {
                        concepto:
                          "Superposiciones",

                        valor:
                          numero(
                            calidad.superposiciones
                          )
                      },

                      {
                        concepto:
                          "Expedientes con antecedentes de prueba",

                        valor:
                          numero(
                            calidad.expedientesConAntecedentesNoConfiables
                          )
                      }
                    ]}
                  />
                </div>
              </div>

              <div
                style={
                  estilos.tarjeta
                }
              >
                {
                  titulo(
                    usandoAgrupados ? "Agrupaciones con mayor actividad" : "Dependencias con mayor actividad"
                  )
                }

                <GraficoBarras
                  datos={
                    dependenciasGrafico.slice(
                      0,
                      12
                    )
                  }

                  campo="expedientesUnicos"

                  etiqueta="Cantidad de expedientes únicos"
                />
              </div>

              <div
                style={
                  estilos.tarjeta
                }
              >
                {
                  titulo(
                    "Expedientes demorados"
                  )
                }

                <div
                  style={{
                    display:
                      "grid",

                    gridTemplateColumns:
                      "repeat(auto-fit,minmax(130px,1fr))",

                    gap:
                      10
                  }}
                >
                  {
                    [
                      [
                        "5 días o más",
                        resumen.demoras?.mas5
                      ],

                      [
                        "10 días o más",
                        resumen.demoras?.mas10
                      ],

                      [
                        "15 días o más",
                        resumen.demoras?.mas15
                      ],

                      [
                        "30 días o más",
                        resumen.demoras?.mas30
                      ]
                    ].map(
                      (
                        [
                          nombre,
                          valor
                        ]
                      ) => (
                        <div
                          key={
                            nombre
                          }

                          style={{
                            background:
                              "#f1f5f9",

                            borderRadius:
                              8,

                            padding:
                              12,

                            textAlign:
                              "center"
                          }}
                        >
                          <div
                            style={{
                              fontSize:
                                11
                            }}
                          >
                            {nombre}
                          </div>

                          <strong
                            style={{
                              fontSize:
                                23
                            }}
                          >
                            {
                              numero(
                                valor
                              )
                            }
                          </strong>
                        </div>
                      )
                    )
                  }
                </div>
              </div>
            </>
          )}

          {pestana ===
            "cierre" &&
            informe.situacionCierre && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Situación histórica al cierre del período"
                )
              }

              <p
                style={{
                  fontSize:
                    13,

                  color:
                    "#64748b"
                }}
              >
                Fecha de corte: {filtros.fechaHasta}. Estos datos deben ser reconstruidos por el controlador con todos los expedientes existentes a esa fecha, incluyendo los que no tuvieron actividad durante el período.
              </p>

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    14
                }}

                onClick={
                  () =>
                    descargarExcel(
                      filasSituacionCierre(
                        informe.situacionCierre
                      ),

                      "SIGEDI_Situacion_Cierre.xlsx",

                      "Situacion cierre"
                    )
                }
              >
                Exportar situación al cierre a Excel
              </button>

              <Tabla
                filas={
                  filasSituacionCierre(
                    informe.situacionCierre
                  )
                }

                columnas={
                  Object.keys(
                    filasSituacionCierre(
                      informe.situacionCierre
                    )[0] ||
                    {}
                  ).map(
                    key => ({
                      key,
                      nombre:
                        key
                    })
                  )
                }
              />
            </div>
          )}

          {pestana ===
            "dependencias" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Estadísticas por dependencia"
                )
              }

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    14
                }}

                onClick={
                  () =>
                    descargarExcel(
                      dependencias,

                      "SIGEDI_Dependencias.xlsx",

                      "Dependencias"
                    )
                }
              >
                Exportar dependencias a Excel
              </button>

              {dependenciasJerarquicas.length > 0 && <div style={{display:'flex',gap:8,marginBottom:14}}>
                <button type="button" style={estilos.boton} onClick={()=>setVistaDependencias('arbol')}>Vista jerárquica</button>
                <button type="button" style={estilos.boton} onClick={()=>setVistaDependencias('individual')}>Vista individual</button>
              </div>}
              {dependenciasJerarquicas.length > 0 && vistaDependencias === 'arbol' &&
                <TablaDependenciasJerarquica filas={dependenciasJerarquicas} detalle={dependencias}/>}
              {(vistaDependencias === 'individual' || !dependenciasJerarquicas.length) && <Tabla
                filas={
                  dependencias
                }

                columnas={[
                  {
                    key:
                      "nombre",

                    nombre:
                      "Dependencia"
                  },

                  {
                    key:
                      "expedientesUnicos",

                    nombre:
                      "Expedientes"
                  },

                  {
                    key:
                      "recibidos",

                    nombre:
                      "Recibidos"
                  },

                  {
                    key:
                      "enviados",

                    nombre:
                      "Enviados"
                  },

                  {
                    key:
                      "archivos",

                    nombre:
                      "Archivos"
                  },

                  {
                    key:
                      "permanenciaPromedioConfiableDias",

                    nombre:
                      "Permanencia (días)",

                    render:
                      d =>
                        decimal(
                          d.permanenciaPromedioConfiableDias
                        )
                  },

                  {
                    key:
                      "transitoPromedioConfiableDias",

                    nombre:
                      "Tránsito (días)",

                    render:
                      d =>
                        decimal(
                          d.transitoPromedioConfiableDias
                        )
                  },

                  {
                    key:
                      "pendientesActuales",

                    nombre:
                      "Pendientes"
                  },

                  {
                    key:
                      "enTransitoActuales",

                    nombre:
                      "En tránsito"
                  },

                  {
                    key:
                      "ubicacionIndeterminada",

                    nombre:
                      "Indeterminados"
                  }
                ]}
              />}
            </div>
          )}

          {pestana === "agrupadas" && (
            <div style={estilos.tarjeta}>
              {titulo("Estadísticas por agrupación institucional")}
              <p style={{ fontSize: 13, color: "#64748b" }}>Los expedientes son únicos dentro de cada grupo. Un expediente puede participar en más de un grupo si circuló por distintas direcciones.</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 15 }}>
                <button style={estilos.botonExcel} onClick={() => descargarExcel(dependenciasAgrupadas.map(({dependenciasIncluidas, ...fila}) => ({...fila, dependenciasIncluidas: (dependenciasIncluidas || []).join(", ")})), "SIGEDI_Dependencias_Agrupadas.xlsx", "Agrupadas")}>Exportar agrupaciones a Excel</button>
                <button style={estilos.boton} onClick={() => setMostrarAgrupados(v => !v)}>{mostrarAgrupados ? "Usar gráfico individual" : "Usar gráfico agrupado"}</button>
              </div>
              <GraficoBarras datos={dependenciasGrafico.slice(0, 20)} campo="expedientesUnicos" etiqueta={usandoAgrupados ? "Expedientes únicos por agrupación" : "Expedientes únicos por dependencia"} />
              {dependenciasJerarquicas.length > 0 && <div style={{marginTop:22,marginBottom:22}}>
                <h4>Árbol de dependencias (clic para desplegar)</h4>
                <TablaDependenciasJerarquica filas={dependenciasJerarquicas} detalle={dependencias}/>
              </div>}
              <div style={{ marginTop: 22 }}>
                <Tabla filas={dependenciasAgrupadas} columnas={[
                  { key: "nombre", nombre: "Dirección / dependencia" },
                  { key: "nivel", nombre: "Nivel" },
                  { key: "cantidadDependencias", nombre: "Destinos incluidos" },
                  { key: "expedientesUnicos", nombre: "Expedientes únicos" },
                  { key: "recibidos", nombre: "Recibidos" },
                  { key: "enviados", nombre: "Enviados" },
                  { key: "archivos", nombre: "Archivos" },
                  { key: "pendientesActuales", nombre: "Pendientes" },
                  { key: "permanenciaPromedioConfiableDias", nombre: "Permanencia (días)", render: d => decimal(d.permanenciaPromedioConfiableDias) },
                  { key: "transitoPromedioConfiableDias", nombre: "Tránsito (días)", render: d => decimal(d.transitoPromedioConfiableDias) }
                ]} />
              </div>
            </div>
          )}

          {pestana ===
            "sedes" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Estadísticas por sede"
                )
              }

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    14
                }}

                onClick={
                  () =>
                    descargarExcel(
                      porSede,

                      "SIGEDI_Sedes.xlsx",

                      "Sedes"
                    )
                }
              >
                Exportar sedes a Excel
              </button>

              <Tabla
                filas={
                  porSede
                }

                columnas={[
                  {
                    key:
                      "nombre",

                    nombre:
                      "Sede"
                  },

                  {
                    key:
                      "expedientesUnicos",

                    nombre:
                      "Expedientes"
                  },

                  {
                    key:
                      "recibidos",

                    nombre:
                      "Recibidos"
                  },

                  {
                    key:
                      "enviados",

                    nombre:
                      "Enviados"
                  },

                  {
                    key:
                      "archivos",

                    nombre:
                      "Archivos"
                  },

                  {
                    key:
                      "permanenciaPromedioConfiableDias",

                    nombre:
                      "Permanencia (días)",

                    render:
                      d =>
                        decimal(
                          d.permanenciaPromedioConfiableDias
                        )
                  },

                  {
                    key:
                      "pendientesActuales",

                    nombre:
                      "Pendientes"
                  },

                  {
                    key:
                      "enTransitoActuales",

                    nombre:
                      "En tránsito"
                  },

                  {
                    key:
                      "ubicacionIndeterminada",

                    nombre:
                      "Indeterminados"
                  }
                ]}
              />
            </div>
          )}

          {pestana ===
            "evolucion" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Evolución mensual"
                )
              }

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    18
                }}

                onClick={
                  () =>
                    descargarExcel(
                      evolucion,

                      "SIGEDI_Evolucion_Mensual.xlsx",

                      "Evolucion"
                    )
                }
              >
                Exportar evolución a Excel
              </button>

              <div
                style={{
                  display:
                    "grid",

                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(250px,1fr))",

                  gap:
                    24,

                  marginBottom:
                    20
                }}
              >
                <GraficoBarras
                  datos={
                    evolucion
                  }

                  campo="iniciados"

                  etiqueta="Expedientes iniciados"
                />

                <GraficoBarras
                  datos={
                    evolucion
                  }

                  campo="salidas"

                  etiqueta="Salidas registradas"
                />

                <GraficoBarras
                  datos={
                    evolucion
                  }

                  campo="entradas"

                  etiqueta="Recepciones registradas"
                />

                <GraficoBarras
                  datos={
                    evolucion
                  }

                  campo="archivos"

                  etiqueta="Archivos digitales"
                />
              </div>

              <Tabla
                filas={
                  evolucion
                }

                columnas={[
                  {
                    key:
                      "mes",

                    nombre:
                      "Mes"
                  },

                  {
                    key:
                      "iniciados",

                    nombre:
                      "Iniciados"
                  },

                  {
                    key:
                      "salidas",

                    nombre:
                      "Salidas"
                  },

                  {
                    key:
                      "entradas",

                    nombre:
                      "Recepciones"
                  },

                  {
                    key:
                      "archivos",

                    nombre:
                      "Archivos"
                  }
                ]}
              />
            </div>
          )}

          {pestana ===
            "demoras" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Expedientes demorados"
                )
              }

              <p
                style={{
                  fontSize:
                    12,

                  color:
                    "#64748b"
                }}
              >
                {
                  numero(
                    demoradosFiltrados.length
                  )
                }{" "}
                expedientes demorados visibles.
              </p>

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    14
                }}

                onClick={
                  () =>
                    descargarExcel(
                      demoradosFiltrados,

                      "SIGEDI_Expedientes_Demorados.xlsx",

                      "Demorados"
                    )
                }
              >
                Exportar demorados a Excel
              </button>

              <Tabla
                filas={
                  demoradosFiltrados
                }

                columnas={[
                  {
                    key:
                      "expedienteID",

                    nombre:
                      "Expediente",

                    render:
                      d => (
                        <EnlaceExpediente
                          id={
                            d.expedienteID
                          }
                        />
                      )
                  },

                  {
                    key:
                      "tipoExpediente",

                    nombre:
                      "Soporte",

                    render:
                      d =>
                        soporteExpediente(
                          expedientes.find(
                            e =>
                              String(
                                e.id
                              ) ===
                              String(
                                d.expedienteID
                              )
                          ) ||
                          d
                        )
                  },

                  {
                    key:
                      "dependencia",

                    nombre:
                      "Dependencia"
                  },

                  {
                    key:
                      "sede",

                    nombre:
                      "Sede"
                  },

                  {
                    key:
                      "desde",

                    nombre:
                      "Desde",

                    render:
                      d =>
                        fechaAR(
                          d.desde
                        )
                  },

                  {
                    key:
                      "dias",

                    nombre:
                      "Días",

                    render:
                      d =>
                        decimal(
                          d.dias
                        )
                  },

                  {
                    key:
                      "nivel",

                    nombre:
                      "Nivel"
                  },

                  {
                    key:
                      "advertenciaHistorica",

                    nombre:
                      "Histórico",

                    render:
                      d =>
                        d.advertenciaHistorica
                          ? "Sí"
                          : "No"
                  }
                ]}
              />
            </div>
          )}

          {pestana ===
            "expedientes" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Consulta individual de expedientes"
                )
              }

              {!informe.expedientes
                ? (
                    <p>
                      Generá el informe con la opción
                      <strong>
                        {" "}
                        Incluir detalle individual
                      </strong>.
                    </p>
                  )
                : (
                    <>
                      <div
                        style={{
                          display:
                            "flex",

                          gap:
                            10,

                          flexWrap:
                            "wrap",

                          marginBottom:
                            14
                        }}
                      >
                        <input
                          style={{
                            ...estilos.input,

                            maxWidth:
                              350
                          }}

                          placeholder="Buscar por ID, iniciador, concepto..."

                          value={
                            busqueda
                          }

                          onChange={
                            e => {
                              setBusqueda(
                                e.target.value
                              );

                              setLimite(
                                50
                              );
                            }
                          }
                        />

                        <button
                          style={
                            estilos.botonExcel
                          }

                          onClick={
                            () =>
                              descargarExcel(
                                prepararExpedientes(
                                  expedientesFiltrados
                                ),

                                "SIGEDI_Expedientes.xlsx",

                                "Expedientes"
                              )
                          }
                        >
                          Exportar expedientes a Excel
                        </button>
                      </div>

                      <p
                        style={{
                          fontSize:
                            12,

                          color:
                            "#64748b"
                        }}
                      >
                        {
                          numero(
                            expedientesFiltrados.length
                          )
                        }{" "}
                        expedientes encontrados.
                      </p>

                      <Tabla
                        filas={
                          expedientesFiltrados.slice(
                            0,
                            limite
                          )
                        }

                        columnas={[
                          {
                            key:
                              "id",

                            nombre:
                              "ID",

                            render:
                              e => (
                                <EnlaceExpediente
                                  id={
                                    e.id
                                  }
                                />
                              )
                          },

                          {
                            key:
                              "fecha",

                            nombre:
                              "Fecha",

                            render:
                              e =>
                                fechaAR(
                                  e.fecha
                                )
                          },

                          {
                            key:
                              "iniciador",

                            nombre:
                              "Iniciador"
                          },

                          {
                            key:
                              "estado",

                            nombre:
                              "Estado"
                          },

                          {
                            key:
                              "tipoExpediente",

                            nombre:
                              "Soporte",

                            render:
                              e =>
                                soporteExpediente(
                                  e
                                )
                          },

                          {
                            key:
                              "dependenciaActual",

                            nombre:
                              "Dependencia actual"
                          },

                          {
                            key:
                              "situacionActual",

                            nombre:
                              "Situación"
                          },

                          {
                            key:
                              "movimientos",

                            nombre:
                              "Movimientos"
                          },

                          {
                            key:
                              "ver",

                            nombre:
                              "Detalle",

                            render:
                              e => (
                                <button
                                  style={{
                                    ...estilos.boton,

                                    padding:
                                      "6px 10px",

                                    fontSize:
                                      12
                                  }}

                                  onClick={
                                    () =>
                                      setExpedienteSeleccionado(
                                        e
                                      )
                                  }
                                >
                                  Ver
                                </button>
                              )
                          }
                        ]}
                      />

                      {
                        limite <
                        expedientesFiltrados.length && (
                          <button
                            style={{
                              ...estilos.boton,

                              marginTop:
                                14
                            }}

                            onClick={
                              () =>
                                setLimite(
                                  n =>
                                    n +
                                    50
                                )
                            }
                          >
                            Mostrar 50 más
                          </button>
                        )
                      }
                    </>
                  )}
            </div>
          )}

          {pestana ===
            "advertencias" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Advertencias por expediente"
                )
              }

              <p
                style={{
                  fontSize:
                    13
                }}
              >
                Expedientes con advertencias:{" "}

                <strong>
                  {
                    numero(
                      advertenciasAgrupadas.length
                    )
                  }
                </strong>

                {" · "}

                Eventos detectados:{" "}

                <strong>
                  {
                    numero(
                      advertenciasAgrupadas.reduce(
                        (
                          total,
                          grupo
                        ) =>
                          total +
                          (
                            grupo.cantidadAdvertencias ||
                            0
                          ),

                        0
                      )
                    )
                  }
                </strong>.
              </p>

              <Tabla
                filas={
                  Object.entries(
                    informe
                      .advertencias
                      ?.porTipo ||
                    {}
                  ).map(
                    (
                      [
                        tipo,
                        cantidad
                      ]
                    ) => ({
                      tipo,
                      cantidad
                    })
                  )
                }

                columnas={[
                  {
                    key:
                      "tipo",

                    nombre:
                      "Tipo"
                  },

                  {
                    key:
                      "cantidad",

                    nombre:
                      "Expedientes"
                  }
                ]}
              />

              <div
                style={{
                  display:
                    "flex",

                  flexWrap:
                    "wrap",

                  gap:
                    10,

                  margin:
                    "18px 0"
                }}
              >
                <select
                  style={{
                    ...estilos.input,

                    maxWidth:
                      280
                  }}

                  value={
                    tipoAdvertencia
                  }

                  onChange={
                    e => {
                      setTipoAdvertencia(
                        e.target.value
                      );

                      setLimite(
                        50
                      );
                    }
                  }
                >
                  <option value="all">
                    Todas las advertencias
                  </option>

                  {
                    tiposAdvertencia.map(
                      t => (
                        <option
                          key={
                            t
                          }

                          value={
                            t
                          }
                        >
                          {t}
                        </option>
                      )
                    )
                  }
                </select>

                <input
                  style={{
                    ...estilos.input,

                    maxWidth:
                      320
                  }}

                  placeholder="Buscar expediente, movimiento o advertencia"

                  value={
                    busqueda
                  }

                  onChange={
                    e => {
                      setBusqueda(
                        e.target.value
                      );

                      setLimite(
                        50
                      );
                    }
                  }
                />

                <button
                  style={
                    estilos.botonExcel
                  }

                  onClick={
                    () =>
                      descargarExcel(
                        prepararAdvertenciasAgrupadas(
                          advertenciasFiltradas
                        ),

                        "SIGEDI_Advertencias.xlsx",

                        "Advertencias"
                      )
                  }
                >
                  Exportar advertencias a Excel
                </button>
              </div>

              {/*
                UNA FILA = UN EXPEDIENTE.
                ACÁ YA NO SE REPITE EL 8261.
              */}

              <Tabla
                filas={
                  advertenciasFiltradas.slice(
                    0,
                    limite
                  )
                }

                columnas={[
                  {
                    key:
                      "expedienteID",

                    nombre:
                      "Expediente",

                    render:
                      grupo => (
                        <EnlaceExpediente
                          id={
                            grupo.expedienteID
                          }
                        />
                      )
                  },

                  {
                    key:
                      "tipoExpediente",

                    nombre:
                      "Soporte",

                    render:
                      grupo =>
                        grupo.tipoExpediente ||
                        soporteExpediente(
                          expedientes.find(
                            e =>
                              String(
                                e.id
                              ) ===
                              String(
                                grupo.expedienteID
                              )
                          ) ||
                          grupo
                        )
                  },

                  {
                    key:
                      "cantidadAdvertencias",

                    nombre:
                      "Advertencias"
                  },

                  {
                    key:
                      "movimientos",

                    nombre:
                      "Movimientos",

                    render:
                      grupo =>
                        grupo.movimientos
                          ?.length
                          ? grupo.movimientos.join(
                              ", "
                            )
                          : "—"
                  },

                  {
                    key:
                      "tipos",

                    nombre:
                      "Tipos",

                    render:
                      grupo =>
                        grupo.tipos
                          ?.length
                          ? grupo.tipos.join(
                              ", "
                            )
                          : "—"
                  },

                  {
                    key:
                      "detalle",

                    nombre:
                      "Detalle",

                    render:
                      grupo => (
                        <details>
                          <summary
                            style={{
                              cursor:
                                "pointer",

                              color:
                                "#164b7e",

                              fontWeight:
                                700,

                              whiteSpace:
                                "nowrap"
                            }}
                          >
                            Ver detalle
                          </summary>

                          <div
                            style={{
                              marginTop:
                                8,

                              minWidth:
                                420
                            }}
                          >
                            {
                              (
                                grupo.detalle ||
                                []
                              ).map(
                                (
                                  item,
                                  index
                                ) => (
                                  <div
                                    key={
                                      `${item.movimientoID || "exp"}-${item.tipo || "tipo"}-${index}`
                                    }

                                    style={{
                                      padding:
                                        "8px 0",

                                      borderBottom:
                                        "1px solid #e5e7eb"
                                    }}
                                  >
                                    <strong>
                                      {
                                        item.movimientoID
                                          ? `Movimiento ${item.movimientoID}`
                                          : "Expediente"
                                      }
                                    </strong>

                                    {
                                      item.tipo
                                        ? ` · ${item.tipo}`
                                        : ""
                                    }

                                    <br />

                                    <span>
                                      {
                                        item.mensaje ||
                                        item.motivo ||
                                        "—"
                                      }
                                    </span>

                                    {item.fechaSalida && (
                                      <>
                                        <br />

                                        <small
                                          style={{
                                            color:
                                              "#64748b"
                                          }}
                                        >
                                          Salida:{" "}
                                          {
                                            fechaAR(
                                              item.fechaSalida
                                            )
                                          }
                                        </small>
                                      </>
                                    )}

                                    {item.fechaEntrada && (
                                      <>
                                        <br />

                                        <small
                                          style={{
                                            color:
                                              "#64748b"
                                          }}
                                        >
                                          Recepción:{" "}
                                          {
                                            fechaAR(
                                              item.fechaEntrada
                                            )
                                          }
                                        </small>
                                      </>
                                    )}

                                    {item.fechaRecepcionInferida && (
                                      <>
                                        <br />

                                        <small
                                          style={{
                                            color:
                                              "#9a6700"
                                          }}
                                        >
                                          Recepción inferida:{" "}
                                          {
                                            fechaAR(
                                              item.fechaRecepcionInferida
                                            )
                                          }
                                        </small>
                                      </>
                                    )}

                                    {
                                      item.diferenciaMinutos !==
                                        null &&
                                      item.diferenciaMinutos !==
                                        undefined && (
                                        <>
                                          <br />

                                          <small
                                            style={{
                                              color:
                                                "#64748b"
                                            }}
                                          >
                                            Diferencia:{" "}
                                            {
                                              decimal(
                                                item.diferenciaMinutos
                                              )
                                            }{" "}
                                            min
                                          </small>
                                        </>
                                      )
                                    }

                                    {item.fecha && (
                                      <>
                                        <br />

                                        <small
                                          style={{
                                            color:
                                              "#64748b"
                                          }}
                                        >
                                          Fecha:{" "}
                                          {
                                            fechaAR(
                                              item.fecha
                                            )
                                          }
                                        </small>
                                      </>
                                    )}
                                  </div>
                                )
                              )
                            }
                          </div>
                        </details>
                      )
                  }
                ]}

                vacio={
                  !informe.expedientes
                    ? "Activá Incluir detalle individual para ver las advertencias."
                    : "No hay expedientes con advertencias."
                }
              />

              {
                limite <
                advertenciasFiltradas.length && (
                  <button
                    style={{
                      ...estilos.boton,

                      marginTop:
                        14
                    }}

                    onClick={
                      () =>
                        setLimite(
                          n =>
                            n +
                            50
                        )
                    }
                  >
                    Mostrar 50 más
                  </button>
                )
              }
            </div>
          )}

          {pestana ===
            "inconsistencias" && (
            <div
              style={
                estilos.tarjeta
              }
            >
              {
                titulo(
                  "Inconsistencias detectadas"
                )
              }

              <p
                style={{
                  fontSize:
                    13
                }}
              >
                {
                  numero(
                    inconsistenciasFiltradas.length
                  )
                }{" "}
                inconsistencias visibles. Las inversiones de hasta 15 minutos se muestran como advertencias menores, no como inconsistencias. Las fechas se presentan en horario argentino.
              </p>

              <button
                style={{
                  ...estilos.botonExcel,

                  marginBottom:
                    14
                }}

                onClick={
                  () =>
                    descargarExcel(
                      inconsistenciasFiltradas,

                      "SIGEDI_Inconsistencias.xlsx",

                      "Inconsistencias"
                    )
                }
              >
                Exportar inconsistencias a Excel
              </button>

              <Tabla
                filas={
                  inconsistenciasFiltradas
                }

                columnas={[
                  {
                    key:
                      "expedienteID",

                    nombre:
                      "Expediente",

                    render:
                      d => (
                        <EnlaceExpediente
                          id={
                            d.expedienteID
                          }
                        />
                      )
                  },

                  {
                    key:
                      "tipoExpediente",

                    nombre:
                      "Soporte",

                    render:
                      d =>
                        soporteExpediente(
                          expedientes.find(
                            e =>
                              String(
                                e.id
                              ) ===
                              String(
                                d.expedienteID
                              )
                          ) ||
                          d
                        )
                  },

                  {
                    key:
                      "movimientoID",

                    nombre:
                      "Movimiento"
                  },

                  {
                    key:
                      "fechaSalida",

                    nombre:
                      "Fecha salida",

                    render:
                      d =>
                        fechaAR(
                          d.fechaSalida
                        )
                  },

                  {
                    key:
                      "fechaEntrada",

                    nombre:
                      "Fecha recepción",

                    render:
                      d =>
                        fechaAR(
                          d.fechaEntrada
                        )
                  },

                  {
                    key:
                      "diferenciaMinutos",

                    nombre:
                      "Diferencia (min)",

                    render:
                      d =>
                        d.diferenciaMinutos ??
                        "—"
                  },

                  {
                    key:
                      "tipo",

                    nombre:
                      "Tipo"
                  },

                  {
                    key:
                      "motivo",

                    nombre:
                      "Motivo"
                  }
                ]}
              />
            </div>
          )}

          {expedienteSeleccionado && (
            <div
              style={{
                position:
                  "fixed",

                inset:
                  0,

                background:
                  "rgba(15,23,42,.65)",

                zIndex:
                  9999,

                display:
                  "flex",

                justifyContent:
                  "center",

                alignItems:
                  "center",

                padding:
                  20
              }}
            >
              <div
                style={{
                  background:
                    "#fff",

                  width:
                    "100%",

                  maxWidth:
                    1000,

                  maxHeight:
                    "90vh",

                  overflowY:
                    "auto",

                  borderRadius:
                    12,

                  padding:
                    22,

                  boxSizing:
                    "border-box"
                }}
              >
                <div
                  style={{
                    display:
                      "flex",

                    justifyContent:
                      "space-between",

                    gap:
                      12,

                    alignItems:
                      "center"
                  }}
                >
                  <h3>
                    Expediente #{expedienteSeleccionado.id}
                  </h3>

                  <button
                    style={
                      estilos.boton
                    }

                    onClick={
                      () =>
                        setExpedienteSeleccionado(
                          null
                        )
                    }
                  >
                    Cerrar
                  </button>
                </div>

                <p>
                  <strong>
                    Concepto:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.concepto ||
                    "—"
                  }
                </p>

                <p>
                  <strong>
                    Iniciador:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.iniciador ||
                    "—"
                  }
                </p>

                <p>
                  <strong>
                    Estado:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.estado
                  }
                </p>

                <p>
                  <strong>
                    Soporte:
                  </strong>{" "}
                  {
                    soporteExpediente(
                      expedienteSeleccionado
                    )
                  }
                </p>

                <p>
                  <strong>
                    Situación:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.situacionActual
                  }
                </p>

                <p>
                  <strong>
                    Dependencia:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.dependenciaActual ||
                    "No determinada"
                  }
                </p>

                <p>
                  <strong>
                    Sede:
                  </strong>{" "}
                  {
                    expedienteSeleccionado.sedeActual ||
                    "No determinada"
                  }
                </p>

                <p>
                  <strong>
                    Último evento:
                  </strong>{" "}
                  {
                    fechaAR(
                      expedienteSeleccionado.ultimoEvento
                    )
                  }
                </p>

                {
                  titulo(
                    "Recorrido y permanencias"
                  )
                }

                <Tabla
                  filas={
                    expedienteSeleccionado.recorrido ||
                    []
                  }

                  columnas={[
                    {
                      key:
                        "dependencia",

                      nombre:
                        "Dependencia"
                    },

                    {
                      key:
                        "sede",

                      nombre:
                        "Sede"
                    },

                    {
                      key:
                        "desde",

                      nombre:
                        "Desde",

                      render:
                        r =>
                          fechaAR(
                            r.desde
                          )
                    },

                    {
                      key:
                        "hasta",

                      nombre:
                        "Hasta",

                      render:
                        r =>
                          fechaAR(
                            r.hasta
                          )
                    },

                    {
                      key:
                        "dias",

                      nombre:
                        "Días totales",

                      render:
                        r =>
                          decimal(
                            r.dias
                          )
                    },

                    {
                      key:
                        "diasPeriodo",

                      nombre:
                        "Días del período",

                      render:
                        r =>
                          decimal(
                            r.diasPeriodo
                          )
                    },

                    {
                      key:
                        "abierta",

                      nombre:
                        "Situación",

                      render:
                        r =>
                          r.abierta
                            ? "Actual"
                            : "Finalizada"
                    }
                  ]}
                />

                <div
                  style={{
                    marginTop:
                      22
                  }}
                >
                  {
                    titulo(
                      "Advertencias del expediente"
                    )
                  }

                  <Tabla
                    filas={
                      expedienteSeleccionado.advertencias ||
                      []
                    }

                    columnas={[
                      {
                        key:
                          "movimientoID",

                        nombre:
                          "Movimiento"
                      },

                      {
                        key:
                          "tipo",

                        nombre:
                          "Tipo"
                      },

                      {
                        key:
                          "mensaje",

                        nombre:
                          "Observación"
                      },

                      {
                        key:
                          "fecha",

                        nombre:
                          "Fecha",

                        render:
                          a =>
                            fechaAR(
                              a.fecha
                            )
                      }
                    ]}
                  />
                </div>

                <div
                  style={{
                    marginTop:
                      18
                  }}
                >
                  <button
                    style={
                      estilos.botonExcel
                    }

                    onClick={
                      () => {
                        const libro =
                          XLSX.utils.book_new();

                        agregarHoja(
                          libro,

                          "Expediente",

                          prepararExpedientes([
                            expedienteSeleccionado
                          ])
                        );

                        agregarHoja(
                          libro,

                          "Recorrido",

                          expedienteSeleccionado.recorrido ||
                          []
                        );

                        agregarHoja(
                          libro,

                          "Advertencias",

                          expedienteSeleccionado.advertencias ||
                          []
                        );

                        XLSX.writeFile(
                          libro,

                          `SIGEDI_Expediente_${expedienteSeleccionado.id}.xlsx`
                        );
                      }
                    }
                  >
                    Exportar este expediente a Excel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}